import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, XCircle, Ban, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { PageHero, Section } from "../components/public/PublicLayout";

const HERO = "https://images.unsplash.com/photo-1605455021703-13b57dcb9efa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const money = (s, v) => `${s?.currency_symbol || "₹"}${v}`;

const STATUS_COLORS = {
  confirmed: "bg-emerald-100 text-emerald-700", completed: "bg-blue-100 text-blue-700",
  payment_pending: "bg-amber-100 text-amber-700", payment_verification: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700", rejected: "bg-red-100 text-red-700",
};

export default function BookingLookup() {
  const s = useSettings();
    const navigate = useNavigate();

  const [bookingId, setBookingId] = useState("");
  const [phone, setPhone] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/bookings/lookup", { booking_id: bookingId, phone });
      setBooking(data);
    } catch (err) {
      setBooking(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    try {
      const { data } = await api.post("/public/bookings/cancel", { booking_id: bookingId, phone, reason: "Devotee requested" });
      setBooking(data);
      if (data.refund_eligible) toast.success(`Cancelled. You are eligible for a refund of ${money(s, data.refund_amount)}.`);
      else toast.info(`Cancelled. ${data.hours_before < data.cancellation_window ? "Not eligible for refund (within " + data.cancellation_window + "h window)." : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
    const allNotes = booking?.admin_notes
    ? String(booking.admin_notes).split("\n").filter(Boolean).map((line) => {
        const m = line.match(/^\[(.*?)\]\s*(.*)$/);
        return m ? m[2] : line;
      })
    : [];
  const refundNotes = allNotes.filter((t) => t.startsWith("Refund ")).slice(-1);
    const otherNotes = allNotes.filter((t) => !t.startsWith("Refund "));

  return (
    <div>
      <PageHero title="View / Cancel Booking" subtitle="Retrieve your seva booking securely" image={HERO} />
      <Section className="max-w-2xl">
         <button
    type="button"
  onClick={() => navigate("/booking?seva=seva-1")}
    className="inline-flex items-center gap-2 mb-4 text-orange-700 hover:text-orange-800 font-semibold transition-colors"
    aria-label="Go back"
    data-testid="lookup-back"
  >
    <ArrowLeft className="w-5 h-5" /> Back
  </button>
        <form onSubmit={lookup} className="bg-white rounded-2xl border border-amber-200 p-6 shadow-sm">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Booking ID</label>
              <input value={bookingId} onChange={(e) => setBookingId(e.target.value.toUpperCase())} placeholder="SMBM-XXXXXX-XXXXX" className="w-full rounded-xl border border-stone-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="lookup-booking-id" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Contact Number</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Registered mobile" className="w-full rounded-xl border border-stone-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="lookup-phone" />
            </div>
          </div>
          <button type="submit" disabled={loading || !bookingId || !phone} className="inline-flex items-center gap-2 mt-4 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 rounded-full disabled:opacity-40 transition-colors" data-testid="lookup-submit">
            <Search className="w-4 h-4" /> {loading ? "Searching…" : "Find Booking"}
          </button>
        </form>

        {booking && (
          <div className="bg-white rounded-2xl border border-amber-200 p-6 shadow-sm mt-6" data-testid="lookup-result">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl font-extrabold text-orange-700">{booking.booking_id}</div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full capitalize ${STATUS_COLORS[booking.booking_status] || "bg-stone-100 text-stone-600"}`}>{booking.booking_status?.replace(/_/g, " ")}</span>
            </div>
            <div className="divide-y divide-amber-50">
              {[["Devotee", booking.devotee_name], ["Seva", booking.seva_name], ["Date", booking.date], ["Time", booking.time_slot], ["Amount", money(s, booking.amount)], ["Payment", booking.payment_status], ["Payment Ref", booking.payment_reference || "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between py-2 text-sm"><span className="text-stone-500">{k}</span><span className="font-semibold text-stone-900 capitalize">{v}</span></div>
              ))}
                          {otherNotes.map((text, i) => (
                <div key={`note-${i}`} className="flex justify-between py-2 text-sm" data-testid="lookup-notes">
                  <span className="text-stone-500">Note</span>
                  <span className="font-semibold text-stone-900 text-right">{text}</span>
                </div>
              ))}
                
            </div>
            {!["cancelled", "rejected", "completed", "refunded"].includes(booking.booking_status) && (
              <button onClick={cancel} className="inline-flex items-center gap-2 mt-5 border border-red-300 text-red-600 font-bold px-5 py-3 rounded-full hover:bg-red-50 transition-colors" data-testid="lookup-cancel">
                <Ban className="w-4 h-4" /> Cancel Booking
              </button>
            )}
          </div>
        )}
        <p className="text-center text-sm text-stone-500 mt-6">Don&apos;t have a booking yet? <Link to="/seva" className="text-orange-700 font-semibold">Book a Seva</Link></p>
      </Section>
    </div>
  );
}
